import { useState } from 'react'
import { Loader2, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useCreateMachineLocation,
  useDeleteMachineLocation,
  useMachineLocations,
  useUpdateMachineLocation,
  type MachineLocation,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  canDeleteMachineLocation,
  canEditMachineLocation,
  formatEvidence,
  getMachineLocationDisplayDescription,
  getMachineLocationDisplayName,
  isAgentMachineLocation,
  sortMachineLocationsForList,
} from '../steps/machine-locations/buildMachineLocationRows'
import {
  EMPTY_MACHINE_LOCATION_FORM,
  isMachineLocationFormValid,
  MachineLocationFormDialog,
  type MachineLocationFormState,
} from '../steps/machine-locations/MachineLocationFormDialog'
import { useMachineLocationGeneration } from '../steps/machine-locations/useMachineLocationGeneration'

type OnboardingMachineLocationsStepProps = {
  complianceId: string
  productId: string | undefined
  onContinue: () => void
}

type LocationListItemProps = {
  location: MachineLocation
  isPending: boolean
  onEdit: (location: MachineLocation) => void
  onDelete: (location: MachineLocation) => void
}

function LocationListItem({
  location,
  isPending,
  onEdit,
  onDelete,
}: LocationListItemProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isEditable = canEditMachineLocation(location)
  const isDeletable = canDeleteMachineLocation(location)
  const displayName = getMachineLocationDisplayName(location, t)
  const displayDescription = getMachineLocationDisplayDescription(location, t)
  const evidenceText = formatEvidence(location.reasoning)
  const showWhyLink = isAgentMachineLocation(location) && evidenceText !== '-'

  const editButton = (
    <Button
      type='button'
      variant='ghost'
      size='icon'
      disabled={isPending || !isEditable}
      aria-label={t('onboarding.machineLocations.editAriaLabel', {
        name: displayName,
      })}
      onClick={() => {
        if (isEditable) {
          onEdit(location)
        }
      }}
    >
      <Pencil className='size-4' />
    </Button>
  )

  const deleteButton = (
    <Button
      type='button'
      variant='ghost'
      size='icon'
      disabled={isPending || !isDeletable}
      aria-label={t('onboarding.machineLocations.deleteAriaLabel', {
        name: displayName,
      })}
      onClick={() => {
        if (isDeletable) {
          onDelete(location)
        }
      }}
    >
      <Trash2 className='size-4' />
    </Button>
  )

  return (
    <li className='rounded-lg border p-4'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0 space-y-1'>
          <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
            <p className='font-medium'>{displayName}</p>
            {showWhyLink ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className='text-xs text-primary underline-offset-4 hover:underline'
                    aria-label={t('onboarding.machineLocations.whyAriaLabel', {
                      name: displayName,
                    })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {t('onboarding.machineLocations.whyLink')}
                  </button>
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-md text-left'>
                  {evidenceText}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          <p className='text-sm whitespace-pre-wrap text-muted-foreground'>
            {displayDescription}
          </p>
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          {isEditable ? (
            editButton
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>{editButton}</span>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-sm'>
                {t('onboarding.machineLocations.systemLocationDisabledTooltip')}
              </TooltipContent>
            </Tooltip>
          )}
          {isDeletable ? (
            deleteButton
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-flex'>{deleteButton}</span>
              </TooltipTrigger>
              <TooltipContent side='top' className='max-w-sm'>
                {t('onboarding.machineLocations.systemLocationDisabledTooltip')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </li>
  )
}

export function OnboardingMachineLocationsStep({
  complianceId,
  productId,
  onContinue,
}: OnboardingMachineLocationsStepProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const { data: locationsData = [], isLoading } =
    useMachineLocations(complianceId)
  const locations = sortMachineLocationsForList(locationsData)
  const createMutation = useCreateMachineLocation(complianceId)
  const updateMutation = useUpdateMachineLocation(complianceId)
  const deleteMutation = useDeleteMachineLocation(complianceId)
  const {
    handleGenerate,
    isRunning,
    statusMessage,
    noNewLocationsMessage,
    generateDisabledDueToSources,
  } = useMachineLocationGeneration({ complianceId, productId })

  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(null)
  const [editingLocation, setEditingLocation] =
    useState<MachineLocation | null>(null)
  const [form, setForm] = useState<MachineLocationFormState>(
    EMPTY_MACHINE_LOCATION_FORM
  )
  const [locationToDelete, setLocationToDelete] =
    useState<MachineLocation | null>(null)

  const isMutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending
  const isLocked = isRunning || isMutationPending

  function openAddDialog() {
    setEditingLocation(null)
    setForm(EMPTY_MACHINE_LOCATION_FORM)
    setDialogMode('add')
  }

  function openEditDialog(location: MachineLocation) {
    setEditingLocation(location)
    setForm({
      name: location.name,
      description: location.description,
    })
    setDialogMode('edit')
  }

  function closeDialog() {
    setDialogMode(null)
    setEditingLocation(null)
    setForm(EMPTY_MACHINE_LOCATION_FORM)
  }

  function handleSaveLocation() {
    if (!isMachineLocationFormValid(form)) {
      return
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
    }

    if (dialogMode === 'add') {
      createMutation.mutate(payload, { onSuccess: closeDialog })
      return
    }

    if (dialogMode === 'edit' && editingLocation != null) {
      updateMutation.mutate(
        {
          machineLocationId: editingLocation.id,
          ...payload,
        },
        { onSuccess: closeDialog }
      )
    }
  }

  function handleConfirmDelete() {
    if (locationToDelete == null) {
      return
    }
    deleteMutation.mutate(locationToDelete.id, {
      onSuccess: () => setLocationToDelete(null),
    })
  }

  const dialogTitle =
    dialogMode === 'edit'
      ? t('onboarding.machineLocations.editTitle')
      : t('onboarding.machineLocations.addTitle')

  const generateButton = (
    <Button
      type='button'
      variant='outline'
      disabled={isLocked || generateDisabledDueToSources}
      onClick={handleGenerate}
    >
      {isRunning ? (
        <Loader2 className='mr-2 size-4 animate-spin' />
      ) : (
        <Sparkles className='mr-2 size-4' />
      )}
      {t('onboarding.machineLocations.generateButton')}
    </Button>
  )

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex min-h-0 flex-1 flex-col gap-6'>
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold tracking-tight'>
            {t('onboarding.machineLocations.title')}
          </h2>
          <p className='leading-relaxed text-muted-foreground'>
            {t('onboarding.machineLocations.description')}
          </p>
        </div>

        <div className='space-y-4'>
          <div className='flex flex-wrap items-center gap-3'>
            {generateDisabledDueToSources ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex'>{generateButton}</span>
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-sm'>
                  {t('onboarding.machineLocations.generateDisabledTooltip')}
                </TooltipContent>
              </Tooltip>
            ) : (
              generateButton
            )}
            <Button
              type='button'
              variant='outline'
              disabled={isLocked}
              onClick={openAddDialog}
            >
              <Plus className='mr-2 size-4' />
              {t('onboarding.machineLocations.addButton')}
            </Button>
          </div>

          {isRunning && statusMessage != null ? (
            <p className='text-sm text-muted-foreground' role='status'>
              {statusMessage}
            </p>
          ) : null}

          {noNewLocationsMessage ? (
            <p className='text-sm text-muted-foreground' role='status'>
              {t('onboarding.machineLocations.noNewLocationsFound')}
            </p>
          ) : null}

          {isLoading && (
            <p className='text-sm text-muted-foreground'>
              {t('onboarding.machineLocations.loading')}
            </p>
          )}

          {!isLoading && locations.length === 0 && (
            <p className='text-sm text-muted-foreground'>
              {t('onboarding.machineLocations.emptyState')}
            </p>
          )}

          {locations.length > 0 && (
            <ul className='space-y-3'>
              {locations.map((location) => (
                <LocationListItem
                  key={location.id}
                  location={location}
                  isPending={isLocked}
                  onEdit={openEditDialog}
                  onDelete={setLocationToDelete}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className='mt-8 flex justify-end pt-6'>
        <Button type='button' disabled={isRunning} onClick={onContinue}>
          {t('onboarding.machineLocations.continue')}
        </Button>
      </div>

      <MachineLocationFormDialog
        open={dialogMode != null}
        title={dialogTitle}
        form={form}
        nameLabel={t('onboarding.machineLocations.nameLabel')}
        descriptionLabel={t('onboarding.machineLocations.descriptionLabel')}
        saveLabel={t('onboarding.machineLocations.save')}
        cancelLabel={t('onboarding.machineLocations.cancel')}
        isPending={isLocked}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
        onFormChange={setForm}
        onSubmit={handleSaveLocation}
      />

      <AlertDialog
        open={locationToDelete != null}
        onOpenChange={(open) => {
          if (!open) {
            setLocationToDelete(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('onboarding.machineLocations.deleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('onboarding.machineLocations.deleteDescription', {
                name:
                  locationToDelete != null
                    ? getMachineLocationDisplayName(locationToDelete, t)
                    : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t('onboarding.machineLocations.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={handleConfirmDelete}
            >
              {t('onboarding.machineLocations.deleteConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
